import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  getContact,
  patchContact,
  getContactNotes,
  addContactNote,
  deleteContactNote,
  getContactReminders,
  addContactReminder,
  updateContactReminder,
  deleteContactReminder,
  getContactProperties,
  linkContactProperty,
  unlinkContactProperty,
  syncContactBuyerPreferences,
  getContactDocuments,
  uploadContactDocument,
  deleteContactDocument,
  convertContactToLead,
  getContactEnquiries,
  getContactOffers,
} from '../../../api/contact';
import { capitalizeEachWord, cleanLeadMessageText, formatOfferSubmissionSummary } from '../../../helper/function';
import { getProperties } from '../../../api/agent';
import { setBuyerPurchased } from '../../analytics/api/analyticsApi';
import ContactSearchStatusPanel from '../components/ContactSearchStatusPanel';
import {
  User, Mail, Phone, MapPin, ArrowLeft, Edit, X,
  FileText, FolderOpen, Home, Calendar, Heart, Gift,
  ListChecks, CheckCircle, Clock, Link, RefreshCw, Plus, Trash2, ExternalLink,
  MessageSquare, DollarSign, Tag, Building2, AlertCircle, TrendingUp, Star, Bot, Sparkles, History,
  Database, Users, Layers
} from 'lucide-react';

import toast from 'react-hot-toast';
import AddressAutocomplete from '../../../components/common/AddressAutocomplete';
import ContactActivityTimeline from '../components/ContactActivityTimeline';
import ContactConversations from '../components/ContactConversations';
import ContactCommunications from '../components/ContactCommunications';
import ContactUnifiedProfile from '../components/ContactUnifiedProfile';
import ContactBuyerPreferencesEditor from '../components/ContactBuyerPreferencesEditor';
import ReminderPresets from '../components/ReminderPresets';
import AgentNotesSummary from '../../../components/notes/AgentNotesSummary';
import ContactChangeLog from '../components/ContactChangeLog';
import {
  datetimeLocalToUtcISOString,
  formatMelbourneDateTime,
  formatMelbourneDatePart,
  formatMelbourneTimePart,
} from '../../../utils/melbourneTime';
import { hasAdminAccess } from "../../../utils/roleAccess";
import { getAgents } from "../../../api/agent";


const SOURCE_LABELS = {
  buyer_portal: 'Buyer Portal',
  website: 'Website',
  referral: 'Referral',
  ofi: 'Open For Inspection',
  social_media: 'Social Media',
  cold_call: 'Cold Call',
  walk_in: 'Walk In',
  // Granular "where did they sign up" sources (set automatically by each flow).
  appraisal_form: 'Appraisal / Sell Form',
  block_form: 'Block of Units Form',
  portal_signup: 'Portal Sign-up',
  enquiry: 'Property Enquiry',
  mortgage_enquiry: 'Mortgage Enquiry',
  pro_tool: 'Pro Tool Interest',
  other: 'Other',
  rea: 'REA',
  domain: 'Domain',
};
const formatSource = (src) =>
  SOURCE_LABELS[src] || (src || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// How each origin entry is drawn in the "Where they came from" card. The
// Database rows lead, because "are they in an agents database?" is the question
// this card was built to answer (Christian Gravias, Monday 13064752019).
const ORIGIN_STYLES = {
  database: { icon: Database, tone: 'text-indigo-500', chip: 'bg-indigo-50 text-indigo-600' },
  portal: { icon: Users, tone: 'text-[#55C5D0]', chip: 'bg-teal-50 text-teal-700' },
  inspection: { icon: Home, tone: 'text-amber-500', chip: 'bg-amber-50 text-amber-700' },
  enquiry: { icon: MessageSquare, tone: 'text-blue-500', chip: 'bg-blue-50 text-blue-700' },
  inbound_lead: { icon: TrendingUp, tone: 'text-pink-500', chip: 'bg-pink-50 text-pink-700' },
  lead: { icon: Layers, tone: 'text-gray-500', chip: 'bg-gray-100 text-gray-600' },
};

const ContactProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const isAdmin = hasAdminAccess(user);
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  // Assignable agents for the admin-only "Assigned Agent" picker (DOMA 13261735033).
  const [agents, setAgents] = useState([]);
  useEffect(() => {
    if (!isAdmin) return;
    getAgents({ role: 'agent', page_size: 200 })
      .then((res) => setAgents(res.data?.results || res.data || []))
      .catch(() => setAgents([]));
  }, [isAdmin]);

  // Portal members' details are admin-editable only. Non-portal contacts
  // (inspection attendees, manually-added people) stay freely editable by agents.
  const hasPortalAccount = !!(contact && (
    contact.has_portal_account ||
    contact.buyer_preferences?.portal_user_found
  ));
  const canEditDetails = isAdmin || !hasPortalAccount;

  // Initialize activeTab from query parameter if present
  const queryParams = new URLSearchParams(location.search);
  const requestedTab = queryParams.get('tab') || 'overview';
  // Activity, Conversations and Emails & Texts are one tab now (Christian
  // Gravias, Monday 13198163292, 7 Oct 2026: "Please move Activity/
  // conversations/emails and texts into 1 tab"). Links elsewhere in the CRM
  // still ask for the old tab names, so they open the merged tab at the
  // matching section.
  const ACTIVITY_SECTION_FOR_TAB = { communications: 'communications', conversations: 'conversations', activity: 'portal' };
  const initialTab = ACTIVITY_SECTION_FOR_TAB[requestedTab] ? 'activity' : requestedTab;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [activitySection, setActivitySection] = useState(ACTIVITY_SECTION_FOR_TAB[requestedTab] || 'communications');

  // When we arrive from an analytics tab (e.g. Calculators), remember where to
  // send "Back" so the user returns to that exact tab instead of the generic
  // Contacts list. Falls back to /contacts when opened any other way.
  const backTo = location.state?.returnTo || '/contacts';
  const backLabel = location.state?.returnLabel || null;

  // Tab-specific states
  const [documents, setDocuments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [lastContacted, setLastContacted] = useState(null);
  const [lastNote, setLastNote] = useState(null);
  const [reminders, setReminders] = useState([]);
  // A booked reminder can be moved or dropped from here. "If you got the date
  // wrong or want to delete it you should be able to" (Simon Abbott,
  // CRM-2026-00074) - the list was read-only apart from ticking one off.
  const [editingReminderId, setEditingReminderId] = useState(null);
  const [editingReminderDate, setEditingReminderDate] = useState('');
  const [savingReminderId, setSavingReminderId] = useState(null);
  const [confirmDeleteReminderId, setConfirmDeleteReminderId] = useState(null);
  const [newReminder, setNewReminder] = useState({ title: '', due_date: '', description: '' });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [linkedProperties, setLinkedProperties] = useState([]);
  const [propertySearch, setPropertySearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [converting, setConverting] = useState(false);

  // Enquiries & Offers state
  const [enquiries, setEnquiries] = useState([]);
  const [enquiriesLoading, setEnquiriesLoading] = useState(false);
  const [offers, setOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);

  // Buyer preferences edit state
  const [editingPrefs, setEditingPrefs] = useState(false);

  useEffect(() => {
    loadContact();
  }, [id]);

  useEffect(() => {
    // Load tab-specific data when tab changes
    if (activeTab === 'filing') loadDocuments();
    if (activeTab === 'notes') {
      loadNotes();
      // Also load enquiries and offers to show their notes
      loadEnquiries();
      loadOffers();
    }
    if (activeTab === 'reminders') loadReminders();
    if (activeTab === 'properties') loadLinkedProperties();
    if (activeTab === 'enquiries') loadEnquiries();
    if (activeTab === 'offers') loadOffers();
    // Buyer preferences are auto-synced on page load, no need to sync on tab change
  }, [activeTab, id]);


  const loadContact = async () => {
    try {
      setLoading(true);
      const response = await getContact(id);
      setContact(response.data);
      
      // Auto-sync buyer preferences from portal on page load (silent, no toast)
      try {
        const prefsResponse = await syncContactBuyerPreferences(id);
        setContact(prev => ({ ...prev, buyer_preferences: prefsResponse.data.buyer_preferences }));
      } catch (prefsError) {
        console.log('Buyer preferences sync skipped:', prefsError?.response?.status || prefsError.message);
      }
    } catch (error) {
      console.error('Error loading contact:', error);
      toast.error('Failed to load contact');
      navigate('/contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateContact = async (data) => {
    try {
      setIsUpdating(true);
      const response = await patchContact(id, data);
      setContact(response.data);
      // The unsubscribe switch gets its own message. "Contact updated" never
      // told the agent whether the person was actually blocked, which is
      // exactly what was asked: "I clicked the unsubscribe button, does this
      // work?" (Christian Gravias, Monday 13064752019).
      const consent = response.data?.consent;
      if ('do_not_contact' in data && consent) {
        if (consent.unsubscribed) {
          toast.success('Unsubscribed. They are now blocked from every email and SMS.');
        } else if (consent.kept?.length) {
          const channels = consent.kept.map((c) => (c === 'email' ? 'email' : 'SMS')).join(' and ');
          toast(`Re-subscribed, but they opted out of ${channels} themselves, so that stays in place.`);
        } else {
          toast.success('Re-subscribed. They can be contacted again.');
        }
      } else {
        toast.success('Contact updated');
      }
    } catch (error) {
      console.error('Error updating contact:', error);
      toast.error('Failed to update contact');
    } finally {
      setIsUpdating(false);
    }
  };

  // "Recently Purchased" — they have bought and are no longer looking, so they
  // drop out of every active buyer list. It used to need a portal account
  // (CRM-2026-00054); the flag now lives on the contact too (CRM-2026-00049), so
  // an open-home walk-in or an imported buyer can be filed away just the same.
  // Reversible: clearing it puts them straight back.
  const [recentlyPurchased, setRecentlyPurchased] = useState(false);
  useEffect(() => { setRecentlyPurchased(!!contact?.is_recently_purchased); }, [contact?.is_recently_purchased]);
  const handleToggleRecentlyPurchased = async () => {
    const next = !recentlyPurchased;
    setRecentlyPurchased(next);
    try {
      await setBuyerPurchased({
        contactId: id,
        buyerId: contact?.portal_buyer_id || undefined,
        purchased: next,
      });
      toast.success(next
        ? 'Marked as Recently Purchased — moved out of active buyers.'
        : 'Moved back to active buyers.');
    } catch (error) {
      setRecentlyPurchased(!next);
      toast.error(error?.response?.data?.error || 'Could not update this buyer.');
    }
  };

  // The agent-entered date of purchase (CRM-2026-00054 revision), so the office
  // knows roughly when to reach out to a past buyer again. Saved on change.
  const [purchaseDate, setPurchaseDate] = useState('');
  useEffect(() => { setPurchaseDate(contact?.purchase_date || ''); }, [contact?.purchase_date]);
  const handlePurchaseDateChange = async (value) => {
    const prev = purchaseDate;
    setPurchaseDate(value);
    try {
      await setBuyerPurchased({
        contactId: id,
        buyerId: contact?.portal_buyer_id || undefined,
        purchased: true,
        purchaseDate: value,
      });
      toast.success(value ? 'Date of purchase saved.' : 'Date of purchase cleared.');
    } catch (error) {
      setPurchaseDate(prev);
      toast.error(error?.response?.data?.error || 'Could not save the date of purchase.');
    }
  };

  const handleConvertToLead = async () => {
    try {
      setConverting(true);
      const response = await convertContactToLead(id);
      toast.success('Converted to Lead');
      if (response.data.lead_id) {
        navigate(`/leads/${response.data.lead_id}`);
      } else {
        loadContact(); // Refresh if no redirect available
      }
    } catch (error) {
      console.error('Error converting to lead:', error);
      toast.error(error.response?.data?.message || 'Failed to convert to lead');
    } finally {
      setConverting(false);
    }
  };

  const loadEnquiries = async () => {
    try {
      setEnquiriesLoading(true);
      const response = await getContactEnquiries(id);
      setEnquiries(response.data || []);
    } catch (error) {
      console.error('Error loading enquiries:', error);
      setEnquiries([]);
    } finally {
      setEnquiriesLoading(false);
    }
  };

  const loadOffers = async () => {
    try {
      setOffersLoading(true);
      const response = await getContactOffers(id);
      setOffers(response.data || []);
    } catch (error) {
      console.error('Error loading offers:', error);
      setOffers([]);
    } finally {
      setOffersLoading(false);
    }
  };

  const loadDocuments = async () => {
    try {
      const response = await getContactDocuments(id);
      setDocuments(response.data?.results || response.data || []);
    } catch (error) {
      console.error('Error loading documents:', error);
      setDocuments([]);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const res = await uploadContactDocument(id, file, file.name);
      setDocuments([res.data, ...documents]);
      toast.success("File uploaded successfully");
    } catch (e) {
      toast.error("Failed to upload file");
    } finally {
      setUploading(false);
      event.target.value = ''; // reset
    }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      await deleteContactDocument(docId);
      setDocuments(documents.filter(d => d.id !== docId));
      toast.success("Document deleted");
    } catch (e) {
      toast.error("Failed to delete document");
    }
  };


  const loadNotes = async () => {
    try {
      const response = await getContactNotes(id);
      // Endpoint now returns { notes, last_contacted } (notes are filtered to the
      // current agent; last_contacted reflects the latest note by ANY agent).
      const data = response.data || {};
      setNotes(Array.isArray(data) ? data : (data.notes || []));
      setLastContacted(Array.isArray(data) ? null : (data.last_contacted || null));
    } catch (error) {
      console.error('Error loading notes:', error);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      const response = await addContactNote(id, { text: newNote });
      setNotes([response.data, ...notes]);
      setLastNote(response.data);
      setLastContacted({ agent_name: response.data.author_name || 'You', date: response.data.created_at, is_yours: true });
      setNewNote('');
      toast.success('Note added');
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await deleteContactNote(id, noteId);
      setNotes(notes.filter(n => n.id !== noteId));
      toast.success('Note deleted');
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const loadReminders = async () => {
    try {
      const response = await getContactReminders(id);
      setReminders(response.data);
    } catch (error) {
      console.error('Error loading reminders:', error);
    }
  };

  const handleAddReminder = async () => {
    if (!newReminder.title || !newReminder.due_date) {
      toast.error('Title and Date are required');
      return;
    }
    try {
      const response = await addContactReminder(id, {
        ...newReminder,
        // due_date comes from a native datetime-local input - a bare
        // "YYYY-MM-DDTHH:MM" string with no timezone info. Sent as-is, Django
        // (TIME_ZONE=UTC) stores it as if it were UTC, so a 10am reminder set
        // by a Melbourne agent redisplays as 8pm/9pm the same day. Convert it
        // as Melbourne wall-clock time first, same as the rest of the app.
        due_date: datetimeLocalToUtcISOString(newReminder.due_date),
      });
      setReminders([...reminders, response.data].sort((a, b) => new Date(a.due_date) - new Date(b.due_date)));
      setNewReminder({ title: '', due_date: '', description: '' });
      toast.success('Reminder added');
    } catch (error) {
      // Surface the real reason rather than a generic message (mirrors ReminderPresets).
      const data = error?.response?.data;
      let msg = 'Failed to add reminder';
      if (typeof data === 'string' && data.trim()) {
        msg = data;
      } else if (data && typeof data === 'object') {
        const first = data.detail || data.error
          || Object.values(data).flat().filter(Boolean)[0];
        if (first) msg = String(first);
      }
      toast.error(msg);
    }
  };

  const toggleReminder = async (reminder) => {
    try {
      const response = await updateContactReminder(id, reminder.id, { is_completed: !reminder.is_completed });
      setReminders(reminders.map(r => r.id === reminder.id ? response.data : r));
    } catch (error) {
      toast.error('Failed to update reminder');
    }
  };

  // Open the inline date editor on a reminder, prefilled with where it sits now
  // (Melbourne wall-clock time, same as it was entered).
  const startEditReminder = (reminder) => {
    setEditingReminderId(reminder.id);
    setConfirmDeleteReminderId(null);
    setEditingReminderDate(
      reminder.due_date
        ? `${formatMelbourneDatePart(reminder.due_date)}T${formatMelbourneTimePart(reminder.due_date)}`
        : ''
    );
  };

  const saveReminderDate = async (reminder) => {
    const dueISO = datetimeLocalToUtcISOString(editingReminderDate);
    if (!dueISO) {
      toast.error('Pick a valid date and time');
      return;
    }
    setSavingReminderId(reminder.id);
    try {
      const response = await updateContactReminder(id, reminder.id, { due_date: dueISO });
      setReminders(reminders
        .map(r => (r.id === reminder.id ? response.data : r))
        .sort((a, b) => new Date(a.due_date) - new Date(b.due_date)));
      setEditingReminderId(null);
      toast.success('Reminder moved');
    } catch (error) {
      toast.error('Failed to move reminder');
    } finally {
      setSavingReminderId(null);
    }
  };

  const removeReminder = async (reminder) => {
    setSavingReminderId(reminder.id);
    try {
      await deleteContactReminder(id, reminder.id);
      setReminders(reminders.filter(r => r.id !== reminder.id));
      setConfirmDeleteReminderId(null);
      if (editingReminderId === reminder.id) setEditingReminderId(null);
      toast.success('Reminder deleted');
    } catch (error) {
      toast.error('Failed to delete reminder');
    } finally {
      setSavingReminderId(null);
    }
  };

  const loadLinkedProperties = async () => {
    try {
      const response = await getContactProperties(id);
      setLinkedProperties(response.data);
    } catch (error) {
      console.error('Error loading properties:', error);
    }
  };


  const searchProperties = async (val) => {
    setPropertySearch(val);
    if (val.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const response = await getProperties({ search: val, page_size: 5 });
      const results = response.data?.results ?? response.data ?? [];
      setSearchResults(Array.isArray(results) ? results : []);
    } catch (error) {
      console.error('Search error:', error);
    }
  };

  const handleLinkProperty = async (propertyId) => {
    try {
      const response = await linkContactProperty(id, { property_id: propertyId, role: 'interested' });
      setLinkedProperties([response.data, ...linkedProperties]);
      setPropertySearch('');
      setSearchResults([]);
      toast.success('Property linked');
    } catch (error) {
      const msg = error.response?.status === 409 ? 'Already linked' : 'Failed to link property';
      toast.error(msg);
    }
  };

  const handleUnlinkProperty = async (linkId) => {
    try {
      await unlinkContactProperty(id, linkId);
      setLinkedProperties(linkedProperties.filter(p => p.id !== linkId));
      toast.success('Property unlinked');
    } catch (error) {
      toast.error('Failed to unlink');
    }
  };

  // When the portal-style editor saves, refresh the contact's display snapshot.
  const handlePreferencesSaved = (snapshot) => {
    if (snapshot) setContact((prev) => ({ ...prev, buyer_preferences: snapshot }));
  };

  const parseAddressString = (addressString) => {
    if (!addressString) return { suburb: '', state: '', postcode: '' };

    const stateAbbreviations = {
      'NSW': 'NSW', 'VIC': 'VIC', 'QLD': 'QLD', 'SA': 'SA',
      'WA': 'WA', 'TAS': 'TAS', 'NT': 'NT', 'ACT': 'ACT'
    };

    const addressParts = addressString.trim().split(',').map(part => part.trim());
    const lastPart = addressParts[addressParts.length - 1];
    const postcodeMatch = lastPart.match(/\b(\d{4})\b/);
    const postcode = postcodeMatch ? postcodeMatch[1] : '';

    let state = '';
    let suburb = '';

    if (postcode) {
      const beforePostcode = lastPart.substring(0, lastPart.indexOf(postcode)).trim();
      const stateMatch = beforePostcode.match(/\b([A-Za-z]{2,4})\b$/i);

      if (stateMatch) {
        const potentialState = stateMatch[1].toUpperCase();
        if (stateAbbreviations[potentialState]) {
          state = potentialState;
          const stateIndex = beforePostcode.toUpperCase().lastIndexOf(potentialState);
          suburb = beforePostcode.substring(0, stateIndex).trim();
        } else {
          suburb = beforePostcode;
        }
      } else {
        suburb = beforePostcode;
      }
    } else {
      const words = lastPart.split(/\s+/);
      if (words.length >= 2) {
        const lastWord = words[words.length - 1].toUpperCase();
        if (stateAbbreviations[lastWord]) {
          state = lastWord;
          suburb = words.slice(0, -1).join(' ');
        } else {
          suburb = lastPart;
        }
      } else {
        suburb = lastPart;
      }
    }

    return {
      suburb: suburb || '',
      state: state || '',
      postcode: postcode || ''
    };
  };

  const handleAddressSelect = (suggestion) => {
    const { raw } = suggestion;
    const addressString = suggestion.description || '';
    const components = raw?.components || raw?.address_components || {};

    let suburb = raw?.suburb || raw?.locality || raw?.suburb_name || raw?.locality_name ||
      raw?.suburb_name_long || raw?.suburb_long ||
      components?.suburb || components?.locality || components?.suburb_name || '';

    let state = raw?.state || raw?.state_code || raw?.state_name || raw?.state_abbreviation ||
      raw?.state_short || components?.state || components?.state_code ||
      components?.state_name || '';

    let postcode = raw?.postcode || raw?.postal_code || raw?.postcode_number || raw?.postal ||
      raw?.postcode_long || components?.postcode || components?.postal_code ||
      components?.postcode_number || '';

    if ((!suburb || !state || !postcode) && addressString) {
      const parsed = parseAddressString(addressString);
      suburb = suburb || parsed.suburb;
      state = state || parsed.state;
      postcode = postcode || parsed.postcode;
    }

    handleUpdateContact({
      address: addressString,
      city: suburb,
      state: state,
      postal_code: postcode
    });
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'profile', label: 'Profile', icon: Sparkles },
    // Emails & texts, Buyer Portal activity and chat conversations in one place (Monday 13198163292).
    { id: 'activity', label: 'Activity/Communications', icon: TrendingUp },
    { id: 'address', label: 'Address', icon: MapPin },
    { id: 'notes', label: 'Notes', icon: FileText },
    { id: 'reminders', label: 'Reminders', icon: Clock },
    { id: 'properties', label: 'Properties', icon: Home },
    { id: 'enquiries', label: 'Enquiries', icon: MessageSquare },
    { id: 'offers', label: 'Offers', icon: DollarSign },
    { id: 'preferences', label: 'Buyer Preferences', icon: Heart },
    { id: 'filing', label: 'Docs', icon: FolderOpen },
    // Audit trail of who changed what on this profile — admins only.
    ...(isAdmin ? [{ id: 'history', label: 'History', icon: History }] : []),
  ];

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#55C5D0]"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      {/* Top Navigation Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between sm:h-20 py-4 sm:py-0 gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 w-full">
              <button
                onClick={() => navigate(backTo)}
                title={backLabel ? `Back to ${backLabel}` : 'Back to Contacts'}
                className="flex items-center gap-1.5 p-2.5 hover:bg-gray-100 rounded-xl transition-all self-start sm:self-center"
              >
                <ArrowLeft className="w-5 h-5 text-gray-500" />
                {backLabel && (
                  <span className="text-sm font-semibold text-gray-600 pr-1">Back to {backLabel}</span>
                )}
              </button>
              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-bold text-gray-900 leading-tight truncate">
                  {contact.name}
                  {contact.preferred_name && (
                    <span className="ml-2 text-sm font-semibold text-gray-500">
                      (goes by {contact.preferred_name})
                    </span>
                  )}
                </h1>
                <div className="flex items-center gap-2 text-sm text-gray-500 font-medium">
                  <span className={`px-2 py-0.5 rounded-md capitalize text-[11px] ${contact.contact_type === 'buyer' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                    {contact.contact_type}
                  </span>
                  {contact.is_buyer_advocate && (
                    <span className="px-2 py-0.5 rounded-md text-[11px] bg-pink-100 text-pink-700 font-semibold">
                      Buyer Advocate
                    </span>
                  )}
                  <span>•</span>
                  {/* A contact mirrored out of a Database CSV is stamped
                      source='other', so the header read "Other" and explained
                      nothing. Say where they really came from. */}
                  <span>
                    {contact.origin?.in_database && !contact.lead_source
                      && (contact.source === 'other' || !contact.source)
                      ? 'Agent Database'
                      : formatSource(contact.lead_source || contact.source)}
                  </span>
                  {/* Where they actually live in the CRM, said in the header so
                      it is answered before the agent has to go looking. */}
                  {contact.origin?.in_database && (
                    <>
                      <span>•</span>
                      <span
                        title={contact.origin.summary}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold bg-indigo-50 text-indigo-600 max-w-[22rem] truncate"
                      >
                        <Database className="w-3 h-3 shrink-0" />
                        {contact.origin.summary}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>

          </div>

          {/* Tab Bar */}
          <div className="flex items-center gap-1 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-3 border-b-2 transition-all whitespace-nowrap text-sm font-bold ${activeTab === tab.id
                  ? 'border-[#55C5D0] text-[#55C5D0]'
                  : 'border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-200'
                  }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">

          {/* Main Content Area */}
          <div className="lg:col-span-2 space-y-6">

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-4 sm:p-6 border-b border-gray-50 flex items-center justify-between">
                  <h3 className="font-bold text-gray-900">General Information</h3>
                  {canEditDetails ? (
                    <button
                      onClick={() => navigate(`/contacts/edit/${contact.id}`)}
                      className="text-sm text-[#55C5D0] font-bold hover:underline"
                    >
                      Edit All Details
                    </button>
                  ) : (
                    <span className="text-xs font-bold text-gray-400 flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" /> Admin-managed
                    </span>
                  )}
                </div>
                {!canEditDetails && (
                  <div className="mx-4 sm:mx-6 mt-4 flex items-start gap-2.5 rounded-xl bg-amber-50 border border-amber-100 px-4 py-3">
                    <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                    <p className="text-[13px] text-amber-800 font-medium leading-snug">
                      This contact has a Buyer Portal account. Their details are read-only —
                      only an admin can update them.
                    </p>
                  </div>
                )}
                <div className="p-4 sm:p-6 grid grid-cols-1 md:grid-cols-2 gap-y-6 sm:gap-y-8 gap-x-12">
                  <InfoItem label="Full Name" value={contact.name} />
                  {contact.preferred_name && (
                    <InfoItem label="Preferred Name" value={contact.preferred_name} />
                  )}
                  <div className="space-y-3">
                    <InfoItem label="Email Address" value={contact.email} isLink href={`mailto:${contact.email}`} />
                    {contact.additional_emails && contact.additional_emails.length > 0 && (
                      <div className="mt-2">
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5">Secondary Emails</label>
                        <div className="flex flex-wrap gap-2">
                          {contact.additional_emails.map((email, idx) => (
                            <a 
                              key={idx}
                              href={`mailto:${email}`}
                              className="px-2 py-1 bg-gray-50 border border-gray-100 text-[#55C5D0] rounded-lg text-xs font-bold hover:bg-gray-100 transition-all"
                            >
                              {email}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Phone numbers: editable inline for non-portal contacts (e.g. inspection
                      attendees); read-only for portal members unless you're an admin. */}
                  {canEditDetails ? (
                    <>
                      <InfoInput label="Mobile Phone" val={contact.phone} onSave={(v) => handleUpdateContact({ phone: v })} />
                      <InfoInput label="Work Phone / Alternate" val={contact.alternate_phone} onSave={(v) => handleUpdateContact({ alternate_phone: v })} />
                    </>
                  ) : (
                    <>
                      <InfoItem label="Mobile Phone" value={contact.phone ? `${contact.phone}${contact.do_not_call ? '  (Do Not Call)' : ''}` : contact.phone} isLink={!!contact.phone && !contact.do_not_call} href={`tel:${contact.phone}`} />
                      <InfoItem label="Work Phone / Alternate" value={contact.alternate_phone ? `${contact.alternate_phone}${contact.do_not_call ? '  (Do Not Call)' : ''}` : contact.alternate_phone} isLink={!!contact.alternate_phone && !contact.do_not_call} href={`tel:${contact.alternate_phone}`} />
                    </>
                  )}
                  {canEditDetails ? (
                    <div>
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-2">Birthday</label>
                      <input
                        type="date"
                        defaultValue={contact.birthday}
                        onBlur={(e) => handleUpdateContact({ birthday: e.target.value })}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20 text-[15px] transition-all"
                      />
                    </div>
                  ) : (
                    <InfoItem label="Birthday" value={contact.birthday} />
                  )}
                  {canEditDetails ? (
                    <div>
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-2">Contact Method</label>
                      <select
                        defaultValue={contact.preferred_contact_method || 'email'}
                        onChange={(e) => handleUpdateContact({ preferred_contact_method: e.target.value })}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20 text-[15px] transition-all capitalize"
                      >
                        <option value="email">Email</option>
                        <option value="phone">Phone</option>
                        <option value="sms">SMS</option>
                      </select>
                    </div>
                  ) : (
                    <InfoItem label="Contact Method" value={contact.preferred_contact_method || 'email'} className="capitalize" />
                  )}

                  <div className="md:col-span-2 flex flex-wrap gap-6 pt-4 border-t border-gray-50">
                    <StatusToggle
                      label="Unsubscribed"
                      isActive={contact.do_not_contact}
                      onToggle={() => handleUpdateContact({ do_not_contact: !contact.do_not_contact })}
                      activeColor="bg-red-500"
                      disabled={!canEditDetails}
                      hint="Blocks every marketing email and SMS to this person, on their address and number, so it survives a re-import."
                    />
                    {/* These two keep their own history (a portal master-
                        unsubscribe, an SMS stop tap), so Unsubscribed does not
                        flip them. Say that it outranks them, or the two green
                        switches read as a contradiction. */}
                    <StatusToggle
                      label="Email Marketing"
                      isActive={contact.email_opt_in && !contact.do_not_contact}
                      onToggle={() => handleUpdateContact({ email_opt_in: !contact.email_opt_in })}
                      disabled={!canEditDetails}
                      hint={contact.do_not_contact ? 'Off while Unsubscribed is on.' : undefined}
                    />
                    <StatusToggle
                      label="SMS Marketing"
                      isActive={contact.sms_opt_in && !contact.do_not_contact}
                      onToggle={() => handleUpdateContact({ sms_opt_in: !contact.sms_opt_in })}
                      disabled={!canEditDetails}
                      hint={contact.do_not_contact ? 'Off while Unsubscribed is on.' : undefined}
                    />
                    {/* Call-specific suppression (CRM-2026-00073): distinct from
                        Unsubscribed (marketing). Drops them off the Who-to-Call
                        queue and flags the call actions here. */}
                    <StatusToggle
                      label="Do Not Call"
                      isActive={contact.do_not_call}
                      onToggle={() => handleUpdateContact({ do_not_call: !contact.do_not_call })}
                      activeColor="bg-red-500"
                      disabled={!canEditDetails}
                      hint="The agent should not phone this person. Removes them from the dashboard Who-to-Call list and flags the call buttons."
                    />
                    <StatusToggle
                      label="Buyer Advocate"
                      isActive={contact.is_buyer_advocate}
                      onToggle={() => handleUpdateContact({ is_buyer_advocate: !contact.is_buyer_advocate })}
                      activeColor="bg-[#db2777]"
                      disabled={!canEditDetails}
                    />
                    <StatusToggle
                      label="Recently Purchased"
                      isActive={recentlyPurchased}
                      onToggle={handleToggleRecentlyPurchased}
                      activeColor="bg-[#55C5D0]"
                    />
                    {recentlyPurchased && (
                      <div className="basis-full flex flex-col gap-1">
                        <label className="text-sm font-medium text-gray-700">
                          Date of purchase
                        </label>
                        <input
                          type="date"
                          value={purchaseDate}
                          onChange={(e) => handlePurchaseDateChange(e.target.value)}
                          className="w-full sm:w-48 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/40"
                        />
                        <span className="text-xs text-gray-400">
                          When they bought — helps the office know when to follow up next.
                        </span>
                      </div>
                    )}
                    {/* "Are you still actually looking?" — the buyer's own
                        answer from the portal or an email link, editable here
                        after a phone call (Monday 13068998532). */}
                    {/* Not gated on canEditDetails: that blocks edits to a
                        portal-owned contact's own details, and a portal buyer
                        ringing to say they've bought is the main case here.
                        Matches the Recently Purchased toggle above. */}
                    <ContactSearchStatusPanel contactId={id} />
                  </div>
                </div>
              </div>
            )}

            {/* Unified Profile Tab */}
            {activeTab === 'profile' && (
              <ContactUnifiedProfile contactId={id} />
            )}

            {/* Activity/Communications Tab: emails & texts, portal activity and conversations */}
            {activeTab === 'activity' && (
              <div className="space-y-6">
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: 'communications', label: 'Emails & Texts', icon: Mail },
                    { id: 'portal', label: 'Buyer Portal Activity', icon: TrendingUp },
                    { id: 'conversations', label: 'Conversations', icon: Bot },
                  ].map((section) => (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => setActivitySection(section.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold border transition-all ${activitySection === section.id
                        ? 'bg-[#55C5D0] border-[#55C5D0] text-white'
                        : 'bg-white border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                      <section.icon className="w-4 h-4" />
                      {section.label}
                    </button>
                  ))}
                </div>

                {activitySection === 'communications' && (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                      <Mail className="w-5 h-5 text-[#55C5D0]" />
                      Emails &amp; Texts
                    </h3>
                    <p className="text-sm text-gray-500 mb-6">
                      Every email and text this contact has been sent, including automated ones such as buyer alerts, open home thank-yous and enquiry confirmations.
                    </p>
                    <ContactCommunications contactId={id} />
                  </div>
                )}

                {activitySection === 'portal' && (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-[#55C5D0]" />
                      Buyer Portal Activity
                    </h3>
                    <p className="text-sm text-gray-500 mb-6">
                      Track real-time interactions from the Buyer Portal, including property views, enquiries, and saved listings.
                    </p>
                    <ContactActivityTimeline contactId={id} />
                  </div>
                )}

                {activitySection === 'conversations' && (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                      <Bot className="w-5 h-5 text-[#55C5D0]" />
                      Chatbot & Messenger Conversations
                    </h3>
                    <p className="text-sm text-gray-500 mb-6">
                      All GeeVee AI and agent messenger conversations synced from the Buyer Portal.
                    </p>
                    <ContactConversations contactId={id} />
                  </div>
                )}
              </div>
            )}

            {/* Address Tab */}
            {activeTab === 'address' && (

              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
                <h3 className="font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-gray-400" />
                  Primary Address
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
                  <div className="md:col-span-2">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-2">Street Address</label>
                    <AddressAutocomplete
                      value={contact.address || ''}
                      onChange={(e) => setContact({ ...contact, address: e.target.value })}
                      onSelect={handleAddressSelect}
                      placeholder="Start typing address..."
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20 text-[15px]"
                    />
                  </div>
                  <InfoInput key={`city-${contact.city}`} label="City" val={contact.city} onSave={(v) => handleUpdateContact({ city: v })} />
                  <InfoInput key={`state-${contact.state}`} label="State" val={contact.state} onSave={(v) => handleUpdateContact({ state: v })} />
                  <InfoInput key={`zip-${contact.postal_code}`} label="Postal Code" val={contact.postal_code} onSave={(v) => handleUpdateContact({ postal_code: v })} />
                  <InfoInput key={`country-${contact.country}`} label="Country" val={contact.country} onSave={(v) => handleUpdateContact({ country: v })} />
                </div>
              </div>
            )}

            {/* Notes Tab */}
            {activeTab === 'notes' && (
              <div className="space-y-6">
                {/* Add New Note */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
                  <div className="flex items-center justify-between gap-4 mb-4">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-[#55C5D0]/10 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-[#55C5D0]" />
                      </div>
                      <h3 className="font-bold text-gray-900">Add Note</h3>
                    </div>
                    {lastContacted && (
                      <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                        <Phone className="w-3.5 h-3.5 text-gray-400" />
                        Last contacted:&nbsp;
                        <span className="font-semibold text-gray-700">
                          {lastContacted.is_yours ? 'You' : lastContacted.agent_name}
                        </span>
                        {lastContacted.date && <> · {new Date(lastContacted.date).toLocaleDateString()}</>}
                      </span>
                    )}
                  </div>
                  <div className="relative">
                    <textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Type a new note here..."
                      className="w-full p-4 pb-14 bg-gray-50 border border-gray-100 rounded-2xl min-h-[180px] focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20 text-[15px]"
                    />
                    <button
                      onClick={handleAddNote}
                      className="absolute bottom-4 right-4 px-6 py-2 bg-[#55C5D0] text-white rounded-xl text-sm font-bold shadow-md hover:bg-[#45B5C0]"
                    >
                      Save Note
                    </button>
                  </div>
                  {/* Schedule a follow-up call — surfaces in Who-to-Call when due */}
                  <div className="mt-4">
                    <ReminderPresets
                      contactId={id}
                      noteId={lastNote?.id || null}
                      noteText={lastNote?.text || ''}
                    />
                  </div>
                </div>

                {/* Agent Notes Section (was "Contact Notes", Monday 13202770775) */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="p-4 sm:p-6 border-b border-gray-50 bg-gradient-to-r from-[#55C5D0]/5 to-white">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#55C5D0]/10 flex items-center justify-center">
                        <FileText className="w-4 h-4 text-[#55C5D0]" />
                      </div>
                      <h3 className="font-bold text-gray-900">Agent Notes</h3>
                      <span className="ml-auto px-2 py-0.5 bg-[#55C5D0]/10 text-[#55C5D0] rounded-full text-xs font-bold">
                        {notes.length}
                      </span>
                    </div>
                  </div>
                  <div className="p-4 sm:p-6 space-y-4">
                    {/* AI summary of this contact's notes (Joshua Diokno, 8 Oct: missing on this tab) */}
                    <AgentNotesSummary notes={notes} subject={contact.name} />
                    {notes.length > 0 ? notes.map((note) => (
                      <div key={note.id} className="bg-gray-50 rounded-xl p-4 group transition-all hover:bg-gray-100/80">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-[#55C5D0]/20 flex items-center justify-center">
                              <User className="w-3.5 h-3.5 text-[#55C5D0]" />
                            </div>
                            <span className="font-bold text-gray-900 text-sm">{note.author_name || 'Agent'}</span>
                            <span className="text-gray-300 text-xs">•</span>
                            <span className="text-gray-400 text-xs">{new Date(note.created_at).toLocaleString()}</span>
                          </div>
                          <button onClick={() => handleDeleteNote(note.id)} className="p-1.5 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-gray-800 text-[15px] leading-relaxed whitespace-pre-wrap pl-9">{note.text}</p>
                      </div>
                    )) : (
                      <p className="text-gray-400 text-sm text-center py-4">No agent notes yet.</p>
                    )}
                  </div>
                </div>

                {/* Enquiry Notes Section */}
                {(() => {
                  // Helper to extract notes from agent_notes or legacy message field
                  const extractEnquiryNotes = (enquiry) => {
                    const notes = [];
                    // First check agent_notes (new format)
                    if (enquiry.agent_notes) {
                      enquiry.agent_notes.split(/\n\n+/).filter(Boolean).forEach(noteText => {
                        const match = noteText.match(/^\[([^\]]+)\]\s*(?:\(([^)]+)\))?\s*(.*)$/s);
                        notes.push({
                          timestamp: match ? match[1] : '',
                          author: match ? (match[2] || 'Agent') : 'Agent',
                          content: match ? match[3] : noteText
                        });
                      });
                    }
                    // Also extract legacy notes from message field (timestamp pattern)
                    if (enquiry.message) {
                      const timestampPattern = /\[(\d{1,2}\/\d{1,2}\/\d{4}\s+\d{1,2}:\d{2}\s*[ap]m)\]\s*([^\[]*)/gi;
                      let match;
                      while ((match = timestampPattern.exec(enquiry.message)) !== null) {
                        const content = match[2].trim();
                        if (content) {
                          notes.push({
                            timestamp: match[1],
                            author: 'Agent',
                            content: content
                          });
                        }
                      }
                    }
                    return notes;
                  };
                  
                  const enquiriesWithNotes = enquiries.map(e => ({
                    ...e,
                    extractedNotes: extractEnquiryNotes(e)
                  })).filter(e => e.extractedNotes.length > 0);

                  return (
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                      <div className="p-4 sm:p-6 border-b border-gray-50 bg-gradient-to-r from-blue-50 to-white">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                            <MessageSquare className="w-4 h-4 text-blue-600" />
                          </div>
                          <h3 className="font-bold text-gray-900">Enquiry Notes</h3>
                          <span className="ml-auto px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-xs font-bold">
                            {enquiriesWithNotes.length}
                          </span>
                        </div>
                      </div>
                      <div className="p-4 sm:p-6 space-y-4">
                        {enquiriesLoading ? (
                          <div className="flex items-center justify-center py-6">
                            <div className="w-6 h-6 border-2 border-blue-200 border-t-blue-500 rounded-full animate-spin" />
                          </div>
                        ) : enquiriesWithNotes.length > 0 ? (
                          enquiriesWithNotes.map((enquiry) => (
                            <div key={enquiry.id} className="bg-blue-50/50 rounded-xl p-4 border border-blue-100/50">
                              <div className="flex items-center gap-2 mb-3">
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-bold uppercase">
                                  {enquiry.reference_number || 'Enquiry'}
                                </span>
                                <span className="text-gray-400 text-xs">
                                  {enquiry.property?.title || enquiry.property?.address || 'Property'}
                                </span>
                              </div>
                              {enquiry.extractedNotes.map((note, idx) => (
                                <div key={idx} className="bg-white rounded-lg p-3 mb-2 last:mb-0 border border-blue-100/30">
                                  <div className="flex items-center gap-2 mb-1">
                                    <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                                      <User className="w-3 h-3 text-blue-600" />
                                    </div>
                                    <span className="text-xs font-bold text-gray-700">{note.author}</span>
                                    {note.timestamp && <span className="text-[10px] text-gray-400 ml-auto">{note.timestamp}</span>}
                                  </div>
                                  <p className="text-sm text-gray-700 leading-relaxed pl-8">{note.content}</p>
                                </div>
                              ))}
                            </div>
                          ))
                        ) : (
                          <p className="text-gray-400 text-sm text-center py-4">No enquiry notes found.</p>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Offer Notes Section */}
                {(() => {
                  // Helper to extract notes from agent_notes or legacy message_to_agent field
                  const extractOfferNotes = (offer) => {
                    const notes = [];
                    // First check agent_notes (new format)
                    if (offer.agent_notes) {
                      offer.agent_notes.split(/\n\n+/).filter(Boolean).forEach(noteText => {
                        const match = noteText.match(/^\[([^\]]+)\]\s*(?:\(([^)]+)\))?\s*(.*)$/s);
                        notes.push({
                          timestamp: match ? match[1] : '',
                          author: match ? (match[2] || 'Agent') : 'Agent',
                          content: match ? match[3] : noteText
                        });
                      });
                    }
                    // Also extract legacy notes from message_to_agent field (timestamp pattern)
                    if (offer.message_to_agent) {
                      const timestampPattern = /\[(\d{1,2}\/\d{1,2}\/\d{4}\s+\d{1,2}:\d{2}\s*[ap]m)\]\s*([^\[]*)/gi;
                      let match;
                      while ((match = timestampPattern.exec(offer.message_to_agent)) !== null) {
                        const content = match[2].trim();
                        if (content) {
                          notes.push({
                            timestamp: match[1],
                            author: 'Agent',
                            content: content
                          });
                        }
                      }
                    }
                    return notes;
                  };
                  
                  const offersWithNotes = offers.map(o => ({
                    ...o,
                    extractedNotes: extractOfferNotes(o)
                  })).filter(o => o.extractedNotes.length > 0);

                  return (
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                      <div className="p-4 sm:p-6 border-b border-gray-50 bg-gradient-to-r from-emerald-50 to-white">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                            <Star className="w-4 h-4 text-emerald-600" />
                          </div>
                          <h3 className="font-bold text-gray-900">Offer Notes</h3>
                          <span className="ml-auto px-2 py-0.5 bg-emerald-100 text-emerald-600 rounded-full text-xs font-bold">
                            {offersWithNotes.length}
                          </span>
                        </div>
                      </div>
                      <div className="p-4 sm:p-6 space-y-4">
                        {offersLoading ? (
                          <div className="flex items-center justify-center py-6">
                            <div className="w-6 h-6 border-2 border-emerald-200 border-t-emerald-500 rounded-full animate-spin" />
                          </div>
                        ) : offersWithNotes.length > 0 ? (
                          offersWithNotes.map((offer) => (
                            <div key={offer.id} className="bg-emerald-50/50 rounded-xl p-4 border border-emerald-100/50">
                              <div className="flex items-center gap-2 mb-3">
                                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-[10px] font-bold uppercase">
                                  {offer.reference_number || 'Offer'}
                                </span>
                                <span className="text-gray-400 text-xs">
                                  {offer.property?.title || offer.property?.address || 'Property'}
                                </span>
                                {offer.offer_amount && (
                                  <span className="ml-auto text-emerald-700 font-bold text-sm">
                                    ${Number(offer.offer_amount).toLocaleString()}
                                  </span>
                                )}
                              </div>
                              {offer.extractedNotes.map((note, idx) => (
                                <div key={idx} className="bg-white rounded-lg p-3 mb-2 last:mb-0 border border-emerald-100/30">
                                  <div className="flex items-center gap-2 mb-1">
                                    <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                                      <User className="w-3 h-3 text-emerald-600" />
                                    </div>
                                    <span className="text-xs font-bold text-gray-700">{note.author}</span>
                                    {note.timestamp && <span className="text-[10px] text-gray-400 ml-auto">{note.timestamp}</span>}
                                  </div>
                                  <p className="text-sm text-gray-700 leading-relaxed pl-8">{note.content}</p>
                                </div>
                              ))}
                            </div>
                          ))
                        ) : (
                          <p className="text-gray-400 text-sm text-center py-4">No offer notes found.</p>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Reminders Tab */}
            {activeTab === 'reminders' && (
              <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
                  <h3 className="font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-gray-400" />
                    Set a Reminder
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="What should we remind you about?"
                      value={newReminder.title}
                      onChange={(e) => setNewReminder({ ...newReminder, title: e.target.value })}
                      className="md:col-span-2 w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20"
                    />
                    <input
                      type="datetime-local"
                      value={newReminder.due_date}
                      onChange={(e) => setNewReminder({ ...newReminder, due_date: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20"
                    />
                    <button
                      onClick={handleAddReminder}
                      className="w-full px-4 py-3 bg-[#55C5D0] text-white rounded-xl font-bold shadow-md hover:bg-[#45B5C0]"
                    >
                      Schedule Reminder
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  {reminders.map((rem) => (
                    <div key={rem.id} className={`bg-white rounded-2xl p-5 border shadow-sm transition-all ${rem.is_completed ? 'opacity-60 border-gray-100 bg-gray-50' : 'border-gray-100 hover:border-[#55C5D0]/30'
                      }`}>
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-4">
                          <button
                            onClick={() => toggleReminder(rem)}
                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${rem.is_completed ? 'bg-[#10B981] border-[#10B981] text-white' : 'border-gray-200 hover:border-[#55C5D0]'
                              }`}
                            title={rem.is_completed ? 'Mark as still to do' : 'Mark as done'}
                          >
                            {rem.is_completed && <CheckCircle className="w-4 h-4" />}
                          </button>
                          <div>
                            <p className={`font-bold text-[15px] ${rem.is_completed ? 'line-through text-gray-400' : 'text-gray-900'}`}>{rem.title}</p>
                            <p className="text-xs text-gray-400">{formatMelbourneDateTime(rem.due_date)} • Assigned to {rem.agent_name}</p>
                          </div>
                        </div>

                        {/* Change the date or drop the reminder - a wrong date used to
                            mean booking a second one on top of it. */}
                        {confirmDeleteReminderId === rem.id ? (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Delete this reminder?</span>
                            <button
                              onClick={() => removeReminder(rem)}
                              disabled={savingReminderId === rem.id}
                              className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-bold hover:bg-red-700 disabled:opacity-50"
                            >
                              {savingReminderId === rem.id ? 'Deleting...' : 'Delete it'}
                            </button>
                            <button
                              onClick={() => setConfirmDeleteReminderId(null)}
                              className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 text-xs font-medium hover:bg-gray-200"
                            >
                              Keep
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => (editingReminderId === rem.id ? setEditingReminderId(null) : startEditReminder(rem))}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-50 text-gray-600 text-xs font-semibold hover:bg-gray-100"
                            >
                              <Edit className="w-3.5 h-3.5" />
                              {editingReminderId === rem.id ? 'Cancel' : 'Change date'}
                            </button>
                            <button
                              onClick={() => setConfirmDeleteReminderId(rem.id)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-red-500 text-xs font-semibold hover:bg-red-50"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>

                      {editingReminderId === rem.id && (
                        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
                          <input
                            type="datetime-local"
                            value={editingReminderDate}
                            onChange={(e) => setEditingReminderDate(e.target.value)}
                            className="px-3 py-2 bg-gray-50 border border-gray-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20"
                          />
                          <button
                            onClick={() => saveReminderDate(rem)}
                            disabled={savingReminderId === rem.id || !editingReminderDate}
                            className="px-4 py-2 bg-[#55C5D0] text-white rounded-xl text-sm font-bold hover:bg-[#45B5C0] disabled:opacity-50"
                          >
                            {savingReminderId === rem.id ? 'Saving...' : 'Save new date'}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                  {reminders.length === 0 && <EmptyState text="No scheduled reminders." />}
                </div>
              </div>
            )}

            {/* Properties Tab */}
            {activeTab === 'properties' && (
              <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
                  <h3 className="font-bold text-gray-900 mb-4">Link a Property</h3>
                  <div className="relative">
                    <Home className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search property by address..."
                      className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20"
                      value={propertySearch}
                      onChange={(e) => searchProperties(e.target.value)}
                    />

                    {searchResults.length > 0 && (
                      <div className="absolute top-[calc(100%+8px)] left-0 right-0 bg-white border border-gray-100 rounded-2xl shadow-xl z-20 overflow-hidden">
                        {searchResults.map((p) => (
                          <button
                            key={p.id}
                            onClick={() => handleLinkProperty(p.id)}
                            className="w-full px-6 py-4 text-left hover:bg-gray-50 transition-all border-b border-gray-50 last:border-0 flex items-center justify-between group"
                          >
                            <div>
                              <p className="font-bold text-gray-900 text-[15px]">{p.title || p.street_address}</p>
                              <p className="text-xs text-gray-400">{p.address || p.suburb}</p>
                            </div>
                            <Plus className="w-5 h-5 text-gray-300 group-hover:text-[#55C5D0]" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {linkedProperties.map((p) => {
                    // A Database owner row is a property this contact owns, but
                    // it is not a CRM listing. It now has a property page of its
                    // own - the Public Property profile - which is what "we also
                    // need a property profile" asked for (Christian Gravias,
                    // CRM-2026-00031). There is still no link to unlink: the row
                    // is removed by editing it in the Database.
                    const fromDatabase = p.source === 'database';
                    const facts = fromDatabase
                      ? [p.property_type, p.beds != null && `${p.beds} bed`, p.baths != null && `${p.baths} bath`,
                         p.car != null && `${p.car} car`, p.land_sqm && `${p.land_sqm} m²`].filter(Boolean).join(' · ')
                      : '';
                    // Where the property came from, said on EVERY card. Tagging
                    // only the Database ones left the CRM ones with nothing but a
                    // role badge, so there was no telling the two apart at a
                    // glance: "It doesn't appear to distinguish which came from a
                    // real listing and the database" (Joshua Diokno,
                    // CRM-2026-00031, 2026-09-07). The colour carries it too,
                    // indigo for a Database row, teal for a real CRM property.
                    const source = fromDatabase
                      ? { label: 'Public Property', badge: 'bg-indigo-50 text-indigo-600',
                          strip: 'bg-indigo-400', icon: 'bg-indigo-50 text-indigo-300',
                          hint: 'A Public Property profile, built from the Database. One layer below your appraisals and listings.' }
                      : { label: 'CRM listing', badge: 'bg-teal-50 text-teal-700',
                          strip: 'bg-[#55C5D0]', icon: 'bg-teal-50 text-teal-300',
                          hint: 'A property record in the CRM.' };
                    return (
                      <div key={p.id} className="relative overflow-hidden bg-white rounded-2xl p-4 sm:p-5 pl-5 sm:pl-6 border border-gray-100 shadow-sm hover:shadow-md transition-all flex justify-between items-start gap-3">
                        <span className={`absolute left-0 top-0 bottom-0 w-1.5 ${source.strip}`} aria-hidden="true" />
                        <div className="flex items-center gap-4 min-w-0">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${source.icon}`}>
                            <Home className="w-6 h-6" />
                          </div>
                          <div className="min-w-0">
                            <p title={p.property_address} className="font-bold text-gray-900 text-[15px] truncate">{p.property_address}</p>
                            {fromDatabase && p.suburb && (
                              <p className="text-xs text-gray-400 truncate">{p.suburb}</p>
                            )}
                            {facts && <p className="text-xs text-gray-400 truncate">{facts}</p>}
                            <div className="flex flex-wrap items-center gap-1.5 mt-1">
                              <span
                                title={source.hint}
                                className={`inline-block px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${source.badge}`}
                              >
                                {source.label}
                              </span>
                              <span className="inline-block px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 text-[10px] font-bold uppercase tracking-wider">{p.role}</span>
                            </div>
                          </div>
                        </div>
                        {fromDatabase ? (
                          <div className="flex flex-col items-end gap-1 shrink-0 pt-1">
                            <RouterLink
                              to={`/public-properties/${p.record_id}`}
                              className="text-[11px] font-bold text-indigo-600 hover:underline"
                              title="Open this property's profile"
                            >
                              Property profile
                            </RouterLink>
                            <RouterLink
                              to={`/database?record=${p.record_id}`}
                              className="text-[11px] font-bold text-gray-400 hover:underline"
                              title="Open the Database row this profile is built from"
                            >
                              In Database
                            </RouterLink>
                          </div>
                        ) : (
                          <button onClick={() => handleUnlinkProperty(p.id)} className="text-gray-300 hover:text-red-500 transition-all p-1 shrink-0">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {linkedProperties.length === 0 && <div className="md:col-span-2"><EmptyState text="No properties linked to this contact." /></div>}
                </div>
              </div>
            )}

            {/* Enquiries Tab */}
            {activeTab === 'enquiries' && (
              <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="p-4 sm:p-6 border-b border-gray-50 flex items-center justify-between bg-gradient-to-r from-blue-50 to-white">
                    <div className="flex items-center gap-3">
                      <MessageSquare className="w-5 h-5 text-blue-500" />
                      <h3 className="font-bold text-gray-900">Submitted Enquiries</h3>
                    </div>
                    <span className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-xs font-bold">
                      {enquiries.length} Total
                    </span>
                  </div>

                  <div className="divide-y divide-gray-50">
                    {enquiriesLoading ? (
                      <div className="p-12 flex flex-col items-center justify-center gap-3">
                        <RefreshCw className="w-8 h-8 text-[#55C5D0] animate-spin" />
                        <p className="text-sm font-bold text-gray-400">Loading enquiries...</p>
                      </div>
                    ) : enquiries.length > 0 ? (
                      enquiries.map((enquiry) => (
                        <div key={enquiry.id} className="p-4 sm:p-6 hover:bg-gray-50/50 transition-colors">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                                <Building2 className="w-6 h-6 text-blue-300" />
                              </div>
                              <div>
                                <h4 className="font-bold text-gray-900 leading-tight mb-1">{enquiry.property_title || 'Unknown Property'}</h4>
                                <div className="flex items-center gap-2 text-xs text-gray-400">
                                  <MapPin className="w-3 h-3" />
                                  <span>{enquiry.property_address}</span>
                                </div>
                                <div className="mt-3 flex flex-wrap gap-2">
                                  <span className="px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 text-[10px] font-bold uppercase tracking-wider">
                                    {enquiry.type_display || enquiry.type}
                                  </span>
                                  {enquiry.status && (
                                    <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${enquiry.status === 'New' ? 'bg-green-50/70 text-green-600' : 'bg-gray-100 text-gray-500'
                                      }`}>
                                      {enquiry.status_display || enquiry.status}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-col sm:items-end gap-2">
                              <span className="text-xs font-bold text-gray-400 flex items-center gap-1.5">
                                <Calendar className="w-3.5 h-3.5" />
                                {new Date(enquiry.created_at).toLocaleDateString('en-AU', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric'
                                })}
                              </span>
                            </div>
                          </div>
                          {enquiry.message && (
                            <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                              <p className="text-sm text-gray-600 italic leading-relaxed">"{cleanLeadMessageText(enquiry.message)}"</p>
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <EmptyState text="No enquiries submitted by this contact." />
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Offers Tab */}
            {activeTab === 'offers' && (
              <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="p-4 sm:p-6 border-b border-gray-50 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-white">
                    <div className="flex items-center gap-3">
                      <Tag className="w-5 h-5 text-emerald-500" />
                      <h3 className="font-bold text-gray-900">Property Offers</h3>
                    </div>
                    <span className="px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full text-xs font-bold">
                      {offers.length} Total
                    </span>
                  </div>

                  <div className="divide-y divide-gray-50">
                    {offersLoading ? (
                      <div className="p-12 flex flex-col items-center justify-center gap-3">
                        <RefreshCw className="w-8 h-8 text-[#55C5D0] animate-spin" />
                        <p className="text-sm font-bold text-gray-400">Loading offers...</p>
                      </div>
                    ) : offers.length > 0 ? (
                      offers.map((offer) => (
                        <div key={offer.id} className="p-4 sm:p-6 hover:bg-gray-50/50 transition-colors">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                                <DollarSign className="w-6 h-6 text-emerald-400" />
                              </div>
                              <div>
                                <h4 className="font-bold text-gray-900 leading-tight mb-1">{offer.property_title || 'Unknown Property'}</h4>
                                <div className="flex items-center gap-2 text-xs text-gray-400">
                                  <MapPin className="w-3 h-3" />
                                  <span>{offer.property_address}</span>
                                </div>
                                <div className="mt-3 flex flex-wrap gap-2">
                                  <span className="px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 text-sm font-black shadow-sm">
                                    {offer.amount_display}
                                  </span>
                                  {offer.status && (
                                    <span className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider ${offer.status === 'ACCEPTED' ? 'bg-green-500 text-white' :
                                      offer.status === 'REJECTED' ? 'bg-red-50 text-red-500' :
                                        'bg-gray-100 text-gray-500'
                                      }`}>
                                      {offer.status_display || offer.status}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-col sm:items-end gap-2">
                              <span className="text-xs font-bold text-gray-400 flex items-center gap-1.5">
                                <Calendar className="w-3.5 h-3.5" />
                                {new Date(offer.created_at).toLocaleDateString('en-AU', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric'
                                })}
                              </span>
                            </div>
                          </div>
                          {(offer.deposit_amount || offer.settlement_days) && (
                            <div className="mt-2 flex flex-wrap gap-4 text-[11px] text-gray-500">
                              {offer.deposit_amount && (
                                <span className="flex items-center gap-1">
                                  <span className="font-semibold text-gray-400 uppercase tracking-tight text-[9px]">Deposit:</span>
                                  <span className="font-bold text-gray-600">${Number(offer.deposit_amount).toLocaleString()}</span>
                                </span>
                              )}
                              {offer.settlement_days && (
                                <span className="flex items-center gap-1">
                                  <span className="font-semibold text-gray-400 uppercase tracking-tight text-[9px]">Settlement:</span>
                                  <span className="font-bold text-gray-600">{offer.settlement_days} Days</span>
                                </span>
                              )}
                            </div>
                          )}
                          {offer.message && (
                            <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                              <p className="text-sm text-gray-600 italic leading-relaxed">"{formatOfferSubmissionSummary(offer.message)}"</p>
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <EmptyState text="No offers submitted by this contact." />
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Buyer Preferences Tab */}
            {activeTab === 'preferences' && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-4 sm:p-6 border-b border-gray-50 flex items-center justify-between bg-gradient-to-r from-pink-50 to-white flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <Heart className="w-5 h-5 text-pink-500" />
                    <h3 className="font-bold text-gray-900">Buyer Preferences</h3>
                  </div>
                  {editingPrefs ? (
                    <button
                      onClick={() => setEditingPrefs(false)}
                      className="px-4 py-2 text-sm font-bold text-gray-500 hover:text-gray-700 flex items-center gap-1.5"
                    >
                      <X className="w-4 h-4" /> Done
                    </button>
                  ) : (
                    <button
                      onClick={() => setEditingPrefs(true)}
                      className="px-4 py-2 bg-white border border-gray-100 text-[#55C5D0] rounded-xl text-sm font-bold flex items-center gap-1.5 hover:bg-gray-50 transition-all shadow-sm"
                    >
                      <Edit className="w-4 h-4" /> Edit Preferences
                    </button>
                  )}
                </div>

                {editingPrefs ? (
                  <ContactBuyerPreferencesEditor contactId={id} onSaved={handlePreferencesSaved} />
                ) : contact.buyer_preferences && Object.keys(contact.buyer_preferences).length > 0 ? (
                  <div className="p-4 sm:p-8 grid grid-cols-1 md:grid-cols-2 gap-y-6 sm:gap-y-10 gap-x-12">
                    <div className="md:col-span-2 flex items-center gap-3 p-4 bg-gray-50 rounded-2xl border border-gray-100">
                      <div className={`w-3 h-3 rounded-full ${contact.buyer_preferences.portal_user_found ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                      <span className="text-sm font-bold text-gray-700">
                        {contact.buyer_preferences.portal_user_found ? 'Verified Portal Account Connected' : 'No Portal Account Matched'}
                      </span>
                    </div>

                    <InfoItem label="Budget Range" value={`${contact.buyer_preferences.budget_min || '$0'} — ${contact.buyer_preferences.budget_max || 'No limit'}`} />
                    <InfoItem label="Preferred Property" value={contact.buyer_preferences.property_type || 'Any'} className="capitalize" />

                    <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-6 py-6 border-y border-gray-50 my-2">
                      <InfoItem label="Bedrooms" value={`${contact.buyer_preferences.bedrooms_min || 'Any'} - ${contact.buyer_preferences.bedrooms_max || 'Any'}`} />
                      <InfoItem label="Bathrooms" value={contact.buyer_preferences.bathrooms || 'Any'} />
                      <InfoItem label="Car Spaces" value={contact.buyer_preferences.car_spaces || 'Any'} />
                      <InfoItem label="Land Size" value={`${contact.buyer_preferences.land_size_min || 'Any'} - ${contact.buyer_preferences.land_size_max || 'Any'}`} />
                      <InfoItem label="Rental Yield" value={contact.buyer_preferences.rental_yield || 'Any'} />
                      <InfoItem label="Capital Growth" value={contact.buyer_preferences.capital_growth || 'Any'} />
                    </div>

                    <div className="md:col-span-2">
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-2">Interested Suburbs</label>
                      <div className="flex flex-wrap gap-2">
                        {contact.buyer_preferences.preferred_suburbs?.map((s, i) => (
                          <span key={i} className="px-3 py-1.5 bg-[#F3F4F6] text-gray-700 rounded-lg text-sm font-bold">{s}</span>
                        ))}
                        {(!contact.buyer_preferences.preferred_suburbs || contact.buyer_preferences.preferred_suburbs.length === 0) && <span className="text-sm text-gray-400">None specified</span>}
                      </div>
                    </div>

                    <InfoItem label="Buyer Type" value={contact.buyer_preferences.buyer_type || 'Not specified'} />
                    <InfoItem label="Buyer Timeline" value={contact.buyer_preferences.timeline || 'Flexible'} />
                    <InfoItem label="Financing Status" value={contact.buyer_preferences.financing_status || 'Unknown'} />
                  </div>
                ) : (
                  <div className="p-20 flex flex-col items-center justify-center">
                    <Mail className="w-12 h-12 text-gray-100 mb-4" />
                    <p className="text-gray-400 font-bold mb-2">No buyer preferences found</p>
                    <p className="text-gray-300 text-sm text-center max-w-md mb-6">
                      No saved portal preferences for this contact yet. Add them manually to track this buyer and feed our stats.
                    </p>
                    <button
                      onClick={() => setEditingPrefs(true)}
                      className="px-5 py-2.5 bg-[#55C5D0] text-white rounded-xl text-sm font-bold shadow-md hover:bg-[#45B5C0] flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" /> Add Preferences
                    </button>
                  </div>
                )}
              </div>
            )}


            {/* Docs Tab */}
            {activeTab === 'filing' && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6 overflow-hidden">
                <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
                  <h3 className="font-bold text-gray-900 flex items-center gap-2">
                    <FolderOpen className="w-5 h-5 text-gray-400" />
                    File Repository
                  </h3>
                  <div className="flex items-center gap-3">
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      onChange={handleFileUpload}
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="px-4 py-2 bg-white border border-gray-100 text-[#55C5D0] rounded-xl text-sm font-bold flex items-center gap-2 hover:bg-gray-50 transition-all active:scale-95 shadow-sm disabled:opacity-50"
                    >
                      {uploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                      Upload File
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {documents.map((doc) => (
                    <div key={doc.id} className="p-4 border border-gray-50 bg-white rounded-xl flex items-center gap-4 hover:shadow-md transition-all group">
                      <div className="w-12 h-12 rounded-lg bg-red-50 flex items-center justify-center">
                        <FileText className="w-6 h-6 text-red-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-gray-900 text-sm truncate">{doc.name || doc.file_name}</p>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider mt-0.5">{doc.category || doc.document_type || 'PDF'}</p>
                      </div>
                      <div className="flex items-center gap-1 opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity">
                        <a
                          href={doc.file}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-gray-400 hover:text-[#55C5D0] transition-all"
                          title="View Document"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                        <button
                          onClick={() => handleDeleteDocument(doc.id)}
                          className="p-2 text-gray-400 hover:text-red-500 transition-all"
                          title="Delete Document"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                {documents.length === 0 && <EmptyState text="No documents attached." />}
              </div>
            )}

            {/* History Tab (admin-only) */}
            {activeTab === 'history' && isAdmin && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <History className="w-5 h-5 text-[#55C5D0]" />
                  Change History
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  Every edit made to this contact's profile — who changed what, and when.
                </p>
                <ContactChangeLog contactId={id} />
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Contact Summary</h4>
              <div className="space-y-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Status</span>
                  <span className="font-bold text-[#55C5D0]">{contact.is_lead ? 'Active Lead' : 'Cold Contact'}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Created</span>
                  <span className="font-bold text-gray-700">{new Date(contact.created_at).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Assigned Agent</span>
                  {isAdmin ? (
                    <select
                      value={contact.agent || ''}
                      onChange={(e) => handleUpdateContact({ agent: e.target.value ? Number(e.target.value) : null })}
                      className="font-bold text-gray-700 bg-transparent text-right focus:outline-none focus:ring-1 focus:ring-[#55C5D0] rounded cursor-pointer"
                      title="Change the agent this contact is assigned to"
                    >
                      <option value="">Unassigned</option>
                      {agents.map((a) => (
                        <option key={a.id} value={a.id}>{a.name || a.email}</option>
                      ))}
                    </select>
                  ) : (
                    <span className="font-bold text-gray-700">{contact.agent_name || 'Unassigned'}</span>
                  )}
                </div>
                {contact.last_contacted && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Last Contacted</span>
                    <span className="font-bold text-gray-700 text-right">
                      {contact.last_contacted.is_yours ? 'You' : contact.last_contacted.agent_name}
                      {contact.last_contacted.date && (
                        <span className="block text-xs font-medium text-gray-400">
                          {new Date(contact.last_contacted.date).toLocaleDateString()}
                        </span>
                      )}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <OriginCard origin={contact.origin} source={contact.lead_source || contact.source} />
          </div>
        </div>
      </div>
    </div>
  );
};

/* Helper Components */

// "Where they came from" — every place in the CRM that actually holds this
// person, Database rows first. Christian Gravias, Monday 13064752019: "can it
// please show where they are in our database in there profile. E.g. are they in
// an agents database? I cant seem to understand where Dom Perri has come from."
// The old answer was the single `source` word, and for anyone mirrored out of a
// Database CSV that word is "Other", which explains nothing.
const OriginCard = ({ origin, source }) => {
  const entries = origin?.entries || [];
  const inDatabase = !!origin?.in_database;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">
        Where they came from
      </h4>
      <p className="text-[11px] text-gray-400 font-medium mb-4">
        Every list and record in the CRM that holds this person.
      </p>

      {/* The headline answer, said in words before the detail rows. */}
      <div className={`flex items-start gap-2.5 rounded-xl px-3 py-2.5 mb-4 ${inDatabase ? 'bg-indigo-50' : 'bg-gray-50'}`}>
        <Database className={`w-4 h-4 mt-0.5 shrink-0 ${inDatabase ? 'text-indigo-500' : 'text-gray-300'}`} />
        <p className={`text-[13px] font-bold leading-snug ${inDatabase ? 'text-indigo-700' : 'text-gray-500'}`}>
          {inDatabase
            ? origin.summary
            : entries.length > 0
              ? `Not in any agent's Database. ${origin.summary}`
              : "Not in any agent's Database."}
        </p>
      </div>

      {entries.length === 0 ? (
        <p className="text-[13px] text-gray-400 font-medium leading-snug">
          Nothing links this contact to a Database list, a portal account, an open
          home or an enquiry. They were most likely added by hand
          {source ? ` (recorded source: ${formatSource(source)})` : ''}.
        </p>
      ) : (
        <div className="space-y-3">
          {entries.map((entry, idx) => {
            const style = ORIGIN_STYLES[entry.kind] || ORIGIN_STYLES.lead;
            const Icon = style.icon;
            const body = (
              <div className="flex items-start gap-3">
                <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${style.tone}`} />
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[13px] font-bold text-gray-900 truncate">{entry.title}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${style.chip}`}>
                      {entry.label}
                    </span>
                  </div>
                  {entry.subtitle && (
                    <p className="text-[12px] text-gray-500 font-medium leading-snug">{entry.subtitle}</p>
                  )}
                  {entry.detail && (
                    <p className="text-[11px] text-gray-400 leading-snug">{entry.detail}</p>
                  )}
                  {entry.date && (
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      Added {formatMelbourneDateTime(entry.date)}
                    </p>
                  )}
                </div>
              </div>
            );
            return entry.link ? (
              <RouterLink
                key={`${entry.kind}-${idx}`}
                to={entry.link}
                className="block rounded-xl -mx-2 px-2 py-1.5 hover:bg-gray-50 transition-all"
                title="Open it"
              >
                {body}
              </RouterLink>
            ) : (
              <div key={`${entry.kind}-${idx}`} className="px-0 py-1.5">{body}</div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const InfoItem = ({ label, value, isLink, href, className = '' }) => (
  <div className={className}>
    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-2">{label}</label>
    {isLink ? (
      <a href={href} className="text-[17px] font-bold text-[#55C5D0] hover:underline decoration-2 underline-offset-4">{value || 'Not provided'}</a>
    ) : (
      <p className="text-[17px] font-bold text-gray-900 leading-tight">{value || 'Not provided'}</p>
    )}
  </div>
);

const InfoInput = ({ label, val, onSave }) => (
  <div>
    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-2">{label}</label>
    <input
      type="text"
      defaultValue={val}
      onBlur={(e) => onSave(e.target.value)}
      placeholder={`Enter ${label}...`}
      className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20 text-[15px] transition-all"
    />
  </div>
);

const StatusToggle = ({ label, isActive, onToggle, activeColor = 'bg-[#10B981]', disabled = false, hint }) => (
  <button
    onClick={disabled ? undefined : onToggle}
    disabled={disabled}
    title={disabled ? 'Only admins can change a portal member’s details' : hint}
    className={`flex items-center gap-3 py-2 px-1 group text-left ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
  >
    <div className={`w-10 h-5 rounded-full relative transition-all shrink-0 ${isActive ? activeColor : 'bg-gray-200'}`}>
      <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${isActive ? 'right-1' : 'left-1'}`}></div>
    </div>
    <span className="min-w-0">
      <span className={`block text-sm font-bold text-gray-600 transition-colors ${disabled ? '' : 'group-hover:text-gray-900'}`}>{label}</span>
      {hint && <span className="block text-[11px] text-gray-400 font-medium leading-snug max-w-[15rem]">{hint}</span>}
    </span>
  </button>
);

const EmptyState = ({ text }) => (
  <div className="py-12 bg-gray-50 rounded-2xl border border-dashed border-gray-200 flex flex-col items-center justify-center text-center">
    <p className="text-sm font-bold text-gray-400">{text}</p>
  </div>
);

export default ContactProfilePage;
